import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import type { ReactNode } from "react";
import { useId } from "react";
import cx from "ui/cx";
import Label from "ui/forms/Label";
import IconCheck from "ui/icons/IconCheck";
import IconDash from "ui/icons/IconDash";
import IconLocked from "ui/icons/IconLocked";
import { wrapWithTooltip } from "ui/popovers/Tooltip";
import styles from "./Checkbox.module.scss";

export interface CheckboxProps extends Omit<React.ComponentProps<typeof CheckboxPrimitive.Root>, "asChild"> {
  label?: ReactNode;
  locked?: boolean;
  readOnly?: boolean;
  className?: string;
  onChangeBoolean?: (checked: boolean) => void;
  tooltip?: ReactNode;
  labelClassName?: string;
}

export default function Checkbox({
  label,
  locked = false,
  readOnly = false,
  disabled = false,
  className,
  labelClassName,
  checked,
  onChangeBoolean, // use for boolean state only
  onCheckedChange, // use for full state w/ indeterminate
  tooltip,
  ...props
}: CheckboxProps) {
  const id = useId();
  const isDisabled = disabled || locked || readOnly;

  const handleCheckedChange = (newChecked: boolean | "indeterminate") => {
    if (!isDisabled) {
      onCheckedChange?.(newChecked);
      if (!onCheckedChange && typeof newChecked === "boolean") {
        onChangeBoolean?.(newChecked);
      }
    }
  };

  const controlled = !onCheckedChange && !onChangeBoolean;

  return wrapWithTooltip(
    <div
      className={cx(
        styles.wrapper,
        isDisabled && styles.disabledBase,
        disabled && styles.disabled,
        locked && styles.locked,
        readOnly && styles.readOnly,
        className,
      )}
      data-testid="checkbox-wrapper"
    >
      <CheckboxPrimitive.Root
        id={id}
        checked={locked ? false : checked}
        onCheckedChange={handleCheckedChange}
        disabled={isDisabled}
        className={styles.root}
        data-testid="checkbox-input"
        {...props}
      >
        <CheckboxPrimitive.Indicator className={styles.indicator} data-testid="checkbox-indicator">
          {checked === "indeterminate" ? <IconDash size={12} /> : <IconCheck size={12} strokeWidth={2} />}
        </CheckboxPrimitive.Indicator>
        {locked && <IconLocked className={styles.lockIcon} data-testid="checkbox-lock-icon" />}
      </CheckboxPrimitive.Root>
      {label && (
        <Label
          // if controlled (no onChange handlers), don't set the htmlFor attribute so that the label is not clickable
          htmlFor={!controlled ? id : undefined}
          disabled={isDisabled}
          data-testid="checkbox-label"
          tooltip={tooltip}
          className={labelClassName}
        >
          {label}
        </Label>
      )}
    </div>,
    // if no label, put the tooltip on the checkbox
    tooltip && !label ? tooltip : undefined,
  );
}
